/**
 * Single source of truth for the customer-facing pricing story.
 *
 * One model, surfaced everywhere (pricing page, paywall, account billing,
 * plan badge, signup gate) so the tiers never drift apart again:
 *
 *   1. Try it      — free credits at signup (freeCreditsSentence), every export
 *                    watermark-free up to 4K, no card.
 *   2. Pay as you go (Credits) — buy a pack, any length. Credits never expire.
 *   3. Enterprise & bulk — teams, seats, brand kit, API, volume pricing,
 *                    managed deployment. Talk to us.
 *
 * Customers see credits only, never money (F8): the live packs come from
 * GET /api/billing/packs and Stripe's checkout page is the one place a price
 * appears.
 */

export type TierId = 'FREE' | 'CREDITS' | 'ENTERPRISE';

export interface TierFeature {
  label: string;
  included: boolean;
}

export interface PricingTier {
  id: TierId;
  name: string;
  /** Headline, e.g. "Free" / "Credits" / "Custom". Never a money amount. */
  price: string;
  /** Small qualifier under the price, e.g. "to start" / "credits never expire". */
  priceSuffix: string;
  tagline: string;
  features: TierFeature[];
  ctaLabel: string;
  highlight?: boolean;
}

/** Credits a new account starts with (D2) when the server doesn't say. */
export const FREE_CREDITS = 50;
/**
 * Credits one short video takes, for every "about N videos" outside the pack cards. Agrees with the
 * packs (150 ≈ 5); the server's price estimate is not used for this count while it reads low.
 */
export const CREDITS_PER_SHORT_VIDEO = 30;

interface PackSize {
  credits: number;
  videosApprox?: number | null;
}

/** About how many short videos `credits` make: a pack's own figure at exactly that pack's size. */
export function videosFor(credits: number, packs: PackSize[] = []): number {
  const pack = packs.find((p) => p.credits === credits && p.videosApprox);
  return pack?.videosApprox ?? Math.round(credits / CREDITS_PER_SHORT_VIDEO);
}

export function aboutVideos(credits: number, packs?: PackSize[]): string {
  const n = videosFor(credits, packs);
  if (n < 1) return 'Less than 1 video';
  return `About ${n} ${n === 1 ? 'video' : 'videos'}`;
}

/** The one free-credit sentence; pass the server's number when it has one. */
export function freeCreditsSentence(credits: number = FREE_CREDITS): string {
  const videos = Math.max(1, videosFor(credits));
  return `Start with ${credits} free credits, enough for about ${videos} short video${videos === 1 ? '' : 's'}.`;
}

export const CREDIT_MODEL_NOTE =
  "Credits pay for what each video uses (the agent's work, narration, images and clips) plus a small fee per finished video. Credits never expire.";

export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'FREE',
    name: 'Try it',
    price: 'Free',
    priceSuffix: 'no card needed',
    tagline: freeCreditsSentence(),
    features: [
      { label: `${FREE_CREDITS} free credits`, included: true },
      { label: 'Every export watermark-free, up to 4K', included: true },
      { label: 'AI script, visuals & narration', included: true },
      { label: 'No card needed', included: true },
    ],
    ctaLabel: 'Start free',
  },
  {
    id: 'CREDITS',
    name: 'Pay as you go',
    price: 'Credits',
    priceSuffix: 'buy a pack · credits never expire',
    tagline: 'Buy a pack of credits and use them whenever. No subscription.',
    features: [
      { label: 'Everything in Try it', included: true },
      { label: 'Any length video', included: true },
      { label: 'Pay for what each video uses', included: true },
      { label: 'Credits never expire', included: true },
    ],
    ctaLabel: 'Buy credits',
    highlight: true,
  },
  {
    id: 'ENTERPRISE',
    name: 'Enterprise & bulk',
    price: 'Custom',
    priceSuffix: 'volume & teams',
    tagline: 'Volume pricing, seats, brand control, and API for teams.',
    features: [
      { label: 'Volume credit pricing', included: true },
      { label: 'Team seats & shared workspace', included: true },
      { label: 'Brand kit & custom templates', included: true },
      { label: 'API access', included: true },
      { label: 'Managed deployment / white-label', included: true },
      { label: 'Priority support', included: true },
    ],
    ctaLabel: 'Contact us',
  },
];

// ── Entitlement → normalized display (badges, billing page) ──────────────────

export type PlanTone = 'free' | 'paid';

export interface PlanDisplay {
  /** Short label: "Free" | "Credits". */
  label: string;
  /** Usage detail, e.g. "3 left", or null when not applicable. */
  detail: string | null;
  tone: PlanTone;
  /** True for a Free user who has run out of credits — nudge to buy. */
  exhausted: boolean;
}

interface EntitlementLike {
  plan: string;
  source?: string;
  videosRemaining: number | null;
  videosUsedPeriod?: number;
  videosPerPeriod?: number | null;
  /** Set when an organization's wallet pays for this member's runs. */
  paidBy?: { name: string } | null;
}

/**
 * Collapse the raw entitlement into the two customer-facing buckets: Credits
 * (a paid source — Stripe pack or an admin credit grant) or Free. Every account
 * is prepaid (F8), so the plan enum is ignored; the paid signal is the source.
 */
export function planDisplay(ent: EntitlementLike): PlanDisplay {
  const remaining = ent.videosRemaining;
  const left =
    remaining !== null && remaining !== undefined
      ? remaining
      : ent.videosPerPeriod != null
      ? ent.videosPerPeriod - (ent.videosUsedPeriod ?? 0)
      : null;
  const detail = left != null ? `${left} left` : null;

  if (ent.paidBy) return { label: ent.paidBy.name, detail, tone: 'paid', exhausted: (left ?? 0) <= 0 };

  if (ent.source === 'STRIPE' || ent.source === 'ADMIN_GRANT') {
    return { label: 'Credits', detail, tone: 'paid', exhausted: (remaining ?? 0) <= 0 };
  }
  const exhausted = (remaining ?? 0) <= 0;
  return { label: 'Free', detail, tone: 'free', exhausted };
}
